import { useMemo, useState, ImgHTMLAttributes } from 'react';
import { TShortStorageItemSummary } from '../../../types/storageTypes';
import { scaledAssetUrl, TAssetScaleOptions } from '../../services/assetResolverService';

type Src = string | TShortStorageItemSummary | null | undefined;

interface IImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src?: Src[] | Src;
  maxHeight?: number;
  maxWidth?: number;
  withSize?: boolean;
  assetScaling?: Partial<TAssetScaleOptions>;
  placeholder?: string;
}

const isStorageItem = (src: Src): src is TShortStorageItemSummary => {
  return typeof src === 'object' && src !== null;
};

export const Image = (allProps: IImageProps) => {
  const { src, maxHeight, maxWidth, placeholder = 'blur', withSize = true, assetScaling, ...props } = allProps;
  const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
  const normalizedSrcList = Array.isArray(src) ? src.filter(Boolean) : [src].filter(Boolean);

  const currentSrc = normalizedSrcList[currentSrcIndex];

  const onError = () => {
    if (currentSrcIndex < normalizedSrcList.length - 1) {
      setCurrentSrcIndex(currentSrcIndex + 1);
    }
  };

  const { width, height } = useMemo(() => {
    if (!isStorageItem(currentSrc) || !currentSrc.width || !currentSrc.height) {
      return {
        width: undefined,
        height: undefined
      };
    }

    let width = currentSrc.width;
    let height = currentSrc.height;

    if (maxWidth && width > maxWidth) {
      const ratio = maxWidth / width;
      width *= ratio;
      height *= ratio;
    }

    if (maxHeight && height > maxHeight) {
      const ratio = maxHeight / height;
      width *= ratio;
      height *= ratio;
    }

    return {
      width,
      height
    };
  }, [currentSrc, maxHeight]);

  let imageUrl = isStorageItem(currentSrc) ? currentSrc.url || '' : currentSrc || '';

  if (assetScaling && imageUrl) {
    imageUrl = scaledAssetUrl(imageUrl, assetScaling);
  }

  return (
    <img
      {...props}
      src={imageUrl}
      onError={onError}
      width={withSize && width ? width : undefined}
      height={withSize && height ? height : undefined}
      style={{ ...props.style, maxWidth, maxHeight }}
    />
  );
};
